import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  Employment,
  Housing,
  LeqFormDto,
  LeqService,
} from '../../service/leq/leq.service';
import { LoggingService } from 'src/app/service/logging-service/logging.service';
import { routeNames } from '../../../assets/val/route-constants';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  PillSelectorOption,
  StepperComponent,
} from 'web-component-library/projects/component-library/src/public-api';
import { FormValidators } from '@app/validation/validators';
import { QueryParamsService } from '@app/service/query-params/query-params.service';
import { Store } from '@ngxs/store';
import {
  LeqType,
  PaymentRequestDto,
} from '@app/model/payment-request/payment-request.dto';

@Component({
  selector: 'app-loan-eligibility-questionnaire',
  templateUrl: './loan-eligibility-questionnaire.component.html',
  styleUrls: [
    './loan-eligibility-questionnaire.component.scss',
    '../page-shared.css',
  ],
})
export class LoanEligibilityQuestionnaireComponent implements OnInit {
  @ViewChild(StepperComponent) stepper!: StepperComponent;

  constructor(
    private store: Store,
    private router: Router,
    private leqService: LeqService,
    private loggingService: LoggingService,
    private queryParamsService: QueryParamsService
  ) {}

  formGroup: FormGroup;
  showError: boolean = false;

  // Pill selector data
  employmentOptions: PillSelectorOption[];
  selectedEmploymentOption: PillSelectorOption;
  housingTypeOptions: PillSelectorOption[];
  selectedHousingTypeOption: PillSelectorOption;

  // Single Pill data
  hasCreditLoan: boolean = false;
  hasStudentLoan: boolean = false;
  hasChildren: boolean = false;

  loading: boolean = true;
  error: boolean = false;

  originalLeq: LeqFormDto;
  currentLeq: LeqFormDto;

  selectedMethodLeqType: LeqType;
  LeqType: typeof LeqType = LeqType;

  navigationInProgress: boolean;

  async ngOnInit(): Promise<void> {
    try {
      this.leqService.fetchLeq().then((resp) => {
        if (resp) {
          this.originalLeq = resp;
        }

        this.initializePillData();
        this.initializeFormGroup();
        this.loading = false;
      });

      this.selectedMethodLeqType = this.leqService.getSelectedMethodLeqType(
        this.queryParamsService.getQueryParams().method,
        this.store.snapshot().paymentRequest.request
      );
    } catch (err) {
      this.error = true;
    }
  }

  initializePillData(): void {
    this.employmentOptions = this.getEmploymentOptions();
    this.housingTypeOptions = this.getHousingTypeOptions();

    this.selectedEmploymentOption =
      this.findSelectedOptionByValue(
        this.employmentOptions,
        this.originalLeq?.employment
      ) ?? this.employmentOptions[0];
    this.selectedHousingTypeOption =
      this.findSelectedOptionByValue(
        this.housingTypeOptions,
        this.originalLeq?.housingType
      ) ?? this.housingTypeOptions[0];

    this.hasCreditLoan = Boolean(this.originalLeq.creditCosts);
    this.hasStudentLoan = Boolean(this.originalLeq.studentLoan);
    this.hasChildren = Boolean(this.originalLeq.numberOfChildren);
  }

  initializeFormGroup(): void {
    this.formGroup = new FormGroup({
      income: new FormControl(String(this.originalLeq?.income || '0'), [
        Validators.required,
        FormValidators.number(0, 1000000),
      ]),
      housingCosts: new FormControl(
        String(this.originalLeq?.housingCosts || '0'),
        [Validators.required, FormValidators.number(0, 1000000)]
      ),
      creditCosts: new FormControl(
        String(this.hasCreditLoan ? this.originalLeq?.creditCosts || '0' : '0'),
        [Validators.required, FormValidators.number(0, 1000000)]
      ),
      studentLoan: new FormControl(
        String(
          this.hasStudentLoan ? this.originalLeq?.studentLoan || '0' : '0'
        ),
        [Validators.required, FormValidators.number(0, 1000000)]
      ),
      miscExpenses: new FormControl(
        String(this.originalLeq?.miscExpenses || '0'),
        [Validators.required, FormValidators.number(0, 1000000)]
      ),
      numberOfChildren: new FormControl(
        String(
          this.hasChildren ? this.originalLeq?.numberOfChildren || '1' : '1'
        ),
        [
          Validators.required,
          FormValidators.number(1, 50),
          Validators.pattern(/^\s*[0-9]+\s*$/),
        ]
      ),
    });
  }

  getEmploymentOptions(): PillSelectorOption[] {
    return [
      { text: 'Heltid', value: Employment.PERMANENT },
      { text: 'Deltid', value: Employment.PART_TIME },
      { text: 'Egenföretagare', value: Employment.ENTREPRENEUR },
      { text: 'Student', value: Employment.STUDENT },
      { text: 'Pensionär', value: Employment.RETIRED },
      { text: 'Arbetslös', value: Employment.UNEMPLOYED },
    ];
  }

  getHousingTypeOptions(): PillSelectorOption[] {
    return [
      { text: 'Hyresrätt', value: Housing.RENTAL },
      { text: 'Bostadsrätt', value: Housing.CONDO },
      { text: 'Villa', value: Housing.HOUSE },
    ];
  }

  findSelectedOptionByValue(
    pillOptions: PillSelectorOption[],
    pillOptionValue: Employment | Housing
  ): PillSelectorOption {
    return pillOptions.find(
      (pillOption) => pillOption.value === pillOptionValue
    );
  }

  handleContinueClick(): void {
    if (this.navigationInProgress) {
      return;
    }

    if (this.stepper.currentStepIndex < this.stepper.steps.length - 1) {
      this.stepper.nextStep();
      return;
    }

    if (this.formGroup.valid) {
      this.setAndSubmitLEQAnswers();
    } else {
      this.stepToFirstError();
      this.showError = true;
    }
  }

  setAndSubmitLEQAnswers(): void {
    this.currentLeq = {
      numberOfChildren: this.hasChildren ? this.getNumberOfChildren() : 0,
      creditCosts: this.hasCreditLoan
        ? this.getNumberFromDecimalString(
            this.formGroup.controls['creditCosts'].value
          )
        : 0,
      housingCosts:
        this.selectedMethodLeqType === LeqType.STANDARD
          ? this.getNumberFromDecimalString(
              this.formGroup.controls['housingCosts'].value
            )
          : null,
      income:
        this.selectedMethodLeqType === LeqType.STANDARD
          ? this.getNumberFromDecimalString(
              this.formGroup.controls['income'].value
            )
          : null,
      miscExpenses:
        this.selectedMethodLeqType === LeqType.STANDARD
          ? this.getNumberFromDecimalString(
              this.formGroup.controls['miscExpenses'].value
            )
          : null,
      employment:
        this.selectedMethodLeqType === LeqType.STANDARD
          ? Employment[this.selectedEmploymentOption.value]
          : null,
      studentLoan: this.hasStudentLoan
        ? this.getNumberFromDecimalString(
            this.formGroup.controls['studentLoan'].value
          )
        : 0,
      housingType:
        this.selectedMethodLeqType === LeqType.STANDARD
          ? Housing[this.selectedHousingTypeOption.value]
          : null,
    };

    this.navigationInProgress = true;
    this.leqService.submitLeq(this.currentLeq).subscribe(
      (res) => {
        this.router
          .navigate([routeNames.LEQ_SUMMARY], {
            queryParamsHandling: 'preserve',
          })
          .then(() => {
            this.navigationInProgress = false;
          });
      },
      (err) => {
        this.navigationInProgress = false;

        this.loggingService.warn(
          'Failed to submit SE LEQ answers due to: ' + JSON.stringify(err)
        );
        console.error(err);
      }
    );
  }

  reroute(): void {
    this.navigationInProgress = true;
    this.router
      .navigate([routeNames.EMAIL_AND_INVOICE], {
        queryParamsHandling: 'merge',
      })
      .then(() => {
        this.navigationInProgress = false;
      });
  }

  handleBackClick(): void {
    // If stepper is first step - route back to email instead.
    if (this.stepper.currentStepIndex !== 0) {
      this.stepper.previousStep();
    } else {
      this.reroute();
    }
  }

  getNumberFromDecimalString(answer?: string): number | undefined {
    if (answer) {
      return Number(answer.replace(',', '.'));
    }
  }

  stepToFirstError(): void {
    const invalidControllers: { name: string; control: AbstractControl }[] =
      Object.keys(this.formGroup.controls)
        .filter((name) => this.formGroup.get(name).invalid)
        .map((key) => ({
          name: key,
          control: this.formGroup.get(key)!,
        }));

    if (invalidControllers.length > 0) {
      const { name, control } = invalidControllers[0];

      // Sends the user to the step that returned the invalid error.
      switch (name) {
        case 'income':
          this.stepper.goToStep(2);
          break;

        case 'housingCosts':
          this.stepper.goToStep(3);
          break;

        case 'creditCosts':
          this.stepper.goToStep(4);
          break;

        case 'studentLoan':
          this.stepper.goToStep(5);
          break;

        case 'miscExpenses':
          this.stepper.goToStep(6);
          break;

        case 'numberOfChildren':
          this.stepper.goToStep(7);
          break;

        default:
          console.warn(`Unhandled invalid control: ${name}`, control.errors);
      }
    }
  }

  getNumberOfChildren(): number {
    let children: number = Number(
      this.formGroup.controls['numberOfChildren'].value
    );

    if (children >= 5) {
      return 5;
    }

    return children;
  }
}
